import { CNS_API_BASE } from "@/config";
import type { CNSResolvedConnection, CNSSourceConnection } from "@/models/sourceConnection";
import { SourceOAuthError } from "@/lib/sources/sourceOAuthErrors";
import { throwOnProblemResponse } from "@/types/problem-detail";

/**
 * Fetches merged enablement rows for the project (flat array with effectiveStatus).
 */
export async function fetchResolvedSourceConnections(
  projectId: string,
): Promise<CNSResolvedConnection[]> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/source-connections/resolved`,
    { credentials: "include" },
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to load plugins");
  }
  return res.json();
}

/**
 * Ensures a credential is enabled as the workspace-default connection.
 * Existing enablement is treated as success.
 */
export async function ensureWorkspaceSourceConnection(
  workspaceId: string,
  credentialId: string,
): Promise<void> {
  const res = await fetch(
    `${CNS_API_BASE}/workspaces/${workspaceId}/source-connections`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credentialPublicId: credentialId, enabled: true }),
    },
  );
  if (res.status === 409) return;
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to enable plugin for this workspace");
  }
}

/** Toggles project or workspace-default enablement for an owned connection row. */
export async function setSourceConnectionEnabled(
  connectionId: string,
  enabled: boolean,
): Promise<CNSSourceConnection> {
  const res = await fetch(`${CNS_API_BASE}/source-connections/${connectionId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json-patch+json" },
    body: JSON.stringify([{ op: "replace", path: "/enabled", value: enabled }]),
  });
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to update plugin enablement");
  }
  return res.json();
}

function findOwnedCredentialRow(
  connections: CNSResolvedConnection[],
  credentialId: string,
): CNSResolvedConnection | null {
  const owned = connections.filter(
    (row) => row.ownedByCurrentUser && row.credentialPublicId === credentialId,
  );
  if (owned.length === 1) return owned[0];
  if (owned.length === 0) return null;
  throw new SourceOAuthError("PLUGIN_ENABLEMENT_FAILED", credentialId);
}

function findRecoveredProjectRow(
  connections: CNSResolvedConnection[],
  credentialId: string,
  projectId: string,
): string | null {
  const owned = connections.filter(
    (row) =>
      row.ownedByCurrentUser &&
      row.credentialPublicId === credentialId &&
      row.enabled &&
      (row.effectiveStatus === "connected" || row.effectiveStatus === "pending") &&
      (row.projectPublicId === projectId || row.projectPublicId == null),
  );
  if (owned.length === 1) {
    return owned[0].connectionPublicId;
  }
  return null;
}

/**
 * Ensures an owned credential is enabled for the project, reusing workspace-default rows when present.
 */
export async function ensureProjectSourceConnection(
  projectId: string,
  credentialId: string,
): Promise<string> {
  const resolved = await fetchResolvedSourceConnections(projectId);
  const owned = findOwnedCredentialRow(resolved, credentialId);

  if (owned?.projectPublicId === projectId && owned.enabled) {
    return owned.connectionPublicId;
  }

  if (owned?.projectPublicId === projectId && !owned.enabled) {
    const patched = await setSourceConnectionEnabled(owned.connectionPublicId, true);
    return patched.id;
  }

  if (owned && owned.projectPublicId == null && owned.enabled) {
    return owned.connectionPublicId;
  }

  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/source-connections`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credentialPublicId: credentialId, enabled: true }),
  });

  if (res.status === 201) {
    const created = (await res.json()) as CNSSourceConnection;
    return created.id;
  }

  if (res.status === 409) {
    const recovered = findRecoveredProjectRow(
      await fetchResolvedSourceConnections(projectId),
      credentialId,
      projectId,
    );
    if (recovered) {
      return recovered;
    }
  }

  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to enable plugin for this project");
  }

  throw new SourceOAuthError("PLUGIN_ENABLEMENT_FAILED", credentialId);
}
